import { useEffect, useState, useCallback, useRef } from 'react'
import { supabase } from '../utils/supabase'
import { getCurrencySymbol } from '../utils/currencyUtils'
import { Alert, Box, CircularProgress, useMediaQuery, useTheme } from '@mui/material'
import { L } from './ui/LavDropdown'
import { SvIc, svDotSx, svPayloadFromForm, svEmptyForm, svFormFromService } from './services/svBits'
import {
	SvDrawer,
	SvmFormScreen,
	SvManageDialog,
	SvmCatsScreen,
	svGhostBtnSx,
	svPrimaryBtnSx,
} from './services/ServicesEditors'

/**
 * ServicesManager — the grooming menu in two tabs (Services / Add-ons), per
 * the Claude Design "Services.html" spec: desktop "Services · workspace" and
 * "Mobile · services". Each tab has its own ordered category list (categories
 * belong to exactly one type), and every item is exclusively a bookable
 * service OR a checkout add-on — the Add/Edit form's Type toggle moves an
 * item between tabs and swaps the category options.
 */

const DANGER = '#BE4B45'
const DANGER_DEEP = '#A53B36'

const catTypeFor = (tab) => (tab === 'addons' ? 'add_on' : 'service')

const sortCats = (list) =>
	[...list].sort((a, b) => {
		if (a.order != null && b.order != null) return a.order - b.order
		if (a.order != null) return -1
		if (b.order != null) return 1
		return a.name.localeCompare(b.name)
	})

const countPillSx = {
	flex: 'none', display: 'inline-flex', alignItems: 'center', height: 21, px: '8px',
	borderRadius: '99px', bgcolor: L.surface, color: '#6E6886', fontSize: '11.5px', fontWeight: 750,
}

const iconBtnSx = {
	width: 30, height: 30, flex: 'none', border: `1px solid ${L.border}`, borderRadius: '8px',
	bgcolor: '#FFFFFF', color: L.muted, display: 'flex', alignItems: 'center',
	justifyContent: 'center', cursor: 'pointer', fontFamily: 'inherit', p: 0,
	'&:hover': { color: L.primaryDeep, borderColor: L.primary },
}

export default function ServicesManager({ merchant }) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))

	const [services, setServices] = useState([])
	const [categories, setCategories] = useState([]) // all categories, both types
	const [loading, setLoading] = useState(false)
	const [busy, setBusy] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const [activeTab, setActiveTab] = useState('services') // 'services' | 'addons'
	const [searchQuery, setSearchQuery] = useState('')
	const [collapsed, setCollapsed] = useState({}) // catId -> true
	const [deletingId, setDeletingId] = useState(null) // service row inline confirm
	const [dragId, setDragId] = useState(null) // category drag (desktop panels)

	// Editor state — {mode:'add'} | {mode:'edit', id}. The form itself lives
	// here (not in the editor components) so the categories manager can be
	// opened from the form's category dropdown without losing typed input.
	const [editor, setEditor] = useState(null)
	const [form, setForm] = useState(null)
	// Which category list the manager is editing: 'service' | 'addon' | null.
	// Not always the active tab — the form's Type toggle can differ from it.
	const [manageType, setManageType] = useState(null)
	// True when the manager was opened from the form's "New category…" row.
	const [manageFromForm, setManageFromForm] = useState(false)

	const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }))

	const openAdd = ({ catId = null, type }) => {
		setForm(svEmptyForm({ catId, type }))
		setEditor({ mode: 'add' })
	}
	const openEdit = (service) => {
		setForm(svFormFromService(service))
		setEditor({ mode: 'edit', id: service.id })
	}
	const closeEditor = () => {
		setEditor(null)
		setForm(null)
	}

	const openManage = (type, fromForm = false) => {
		setManageType(type)
		setManageFromForm(fromForm)
	}
	const closeManage = () => {
		setManageType(null)
		setManageFromForm(false)
	}

	// Mobile screen: 'list' | 'form' | 'cats'. Categories takes precedence so
	// it can be reached from the form; backing out returns to the form.
	const mobileScreen = isMobile
		? (manageType ? 'cats' : editor ? 'form' : 'list')
		: 'list'

	const loadData = useCallback(async () => {
		try {
			const [catRes, svcRes] = await Promise.all([
				supabase
					.from('categories')
					.select('id, name, order, category_type')
					.eq('merchant_id', merchant.id),
				supabase
					.from('service_types')
					.select('id, name, duration_minutes, category_id, appointment_color, price, description, is_service, is_add_on')
					.eq('merchant_id', merchant.id)
					.order('name'),
			])
			if (catRes.error) throw catRes.error
			if (svcRes.error) throw svcRes.error
			setCategories(sortCats(catRes.data || []))
			setServices(svcRes.data || [])
		} catch (e) {
			setErrorMessage(e.message || 'Failed to load services')
		}
	}, [merchant?.id])

	useEffect(() => {
		if (!merchant?.id) return
		setLoading(true)
		loadData().finally(() => setLoading(false))
	}, [merchant?.id, loadData])

	const cs = getCurrencySymbol(merchant?.currency)
	const formatPrice = (cents) => `${cs}${((cents || 0) / 100).toFixed(2)}`
	// Add-ons may be 0 minutes (cost, but no extra time on the appointment).
	const formatDuration = (mins) => (mins ? `${mins} min` : 'No extra time')

	// Categories are split by type — each tab shows only its own list.
	const catsByType = {
		service: categories.filter((c) => c.category_type !== 'add_on'),
		addon: categories.filter((c) => c.category_type === 'add_on'),
	}
	const tabCats = activeTab === 'addons' ? catsByType.addon : catsByType.service
	// The list the manage surface is editing (may differ from the active tab).
	const managedCats = manageType === 'addon' ? catsByType.addon : catsByType.service

	const catNameById = {}
	categories.forEach((c) => { catNameById[c.id] = c.name })

	// Tab + search filtering (search matches name, category name, description).
	const q = searchQuery.trim().toLowerCase()
	const typedItems = services.filter((item) =>
		activeTab === 'addons' ? item.is_add_on : item.is_service
	)
	const visibleItems = typedItems.filter((item) => {
		if (!q) return true
		return (
			item.name.toLowerCase().includes(q) ||
			(catNameById[item.category_id] || '').toLowerCase().includes(q) ||
			(item.description || '').toLowerCase().includes(q)
		)
	})

	// Ordered groups: every category of the active tab (even empty), plus an
	// Uncategorized bucket for items without a matching category.
	let groups = tabCats.map((c) => ({
		cat: c,
		items: visibleItems.filter((s) => s.category_id === c.id),
	}))
	const loose = visibleItems.filter(
		(s) => !s.category_id || !tabCats.some((c) => c.id === s.category_id)
	)
	if (loose.length) groups.push({ cat: { id: null, name: 'Uncategorized' }, items: loose })
	if (q) groups = groups.filter((g) => g.items.length > 0)

	const counts = {
		services: services.filter((s) => s.is_service).length,
		addons: services.filter((s) => s.is_add_on).length,
	}

	// Count against ALL services (not search-filtered) — drives category delete gating.
	const countFor = (catId) => services.filter((s) => s.category_id === catId).length

	// ── category ops (persisted immediately) ──
	const renameCat = async (id, name) => {
		try {
			const { error } = await supabase.from('categories').update({ name }).eq('id', id)
			if (error) throw error
			await loadData()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to rename category')
		}
	}

	// Adds to the list the manage surface is currently editing. When it was
	// opened from the form's category dropdown, the new category is selected
	// straight away — that's what the user was after.
	const addCat = async (name) => {
		setBusy(true)
		try {
			const { data, error } = await supabase
				.from('categories')
				.insert({
					merchant_id: merchant.id,
					name,
					order: managedCats.length,
					category_type: manageType === 'addon' ? 'add_on' : 'service',
				})
				.select('id')
				.single()
			if (error) throw error
			await loadData()
			if (manageFromForm && data?.id && form && form.type === manageType) {
				setField('catId', data.id)
			}
		} catch (e) {
			setErrorMessage(e.message || 'Failed to add category')
		} finally {
			setBusy(false)
		}
	}

	const deleteCat = async (id) => {
		setBusy(true)
		try {
			const { error } = await supabase.from('categories').delete().eq('id', id)
			if (error) throw error
			await loadData()
			// Don't leave the open form pointing at a category that's gone.
			if (form?.catId === id) setField('catId', null)
		} catch (e) {
			setErrorMessage(e.message || 'Failed to delete category')
		} finally {
			setBusy(false)
		}
	}

	// Live drag reorder — updates local state while dragging; order is
	// persisted when the drag ends.
	const reorderCat = (fromId, toId) => {
		setCategories((prev) => {
			const from = prev.findIndex((c) => c.id === fromId)
			const to = prev.findIndex((c) => c.id === toId)
			if (from < 0 || to < 0 || from === to) return prev
			const next = [...prev]
			const [moved] = next.splice(from, 1)
			next.splice(to, 0, moved)
			return next
		})
	}

	// Ref mirror so drag-end persistence always sees the post-reorder list,
	// even if the final dragover's setState hasn't re-rendered yet.
	const categoriesRef = useRef(categories)
	useEffect(() => { categoriesRef.current = categories }, [categories])

	const persistCatOrder = async () => {
		// Re-derive the list being reordered (the manage surface's, or the active
		// tab's when dragging panels) from the latest state; write order = index.
		const type = manageType || catTypeFor(activeTab)
		const list = (type === 'addon' || type === 'add_on'
			? categoriesRef.current.filter((c) => c.category_type === 'add_on')
			: categoriesRef.current.filter((c) => c.category_type !== 'add_on'))
		try {
			await Promise.all(
				list.map((c, idx) =>
					supabase.from('categories').update({ order: idx }).eq('id', c.id)
				)
			)
			await loadData()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to save category order')
		}
	}

	const moveCat = async (id, dir) => {
		const i = managedCats.findIndex((c) => c.id === id)
		const j = i + dir
		if (i < 0 || j < 0 || j >= managedCats.length) return
		const next = [...managedCats]
		;[next[i], next[j]] = [next[j], next[i]]
		setBusy(true)
		try {
			await Promise.all(
				next.map((c, idx) =>
					supabase.from('categories').update({ order: idx }).eq('id', c.id)
				)
			)
			await loadData()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to reorder categories')
		} finally {
			setBusy(false)
		}
	}

	// ── service ops ──
	const saveService = async (form, id) => {
		setBusy(true)
		setErrorMessage('')
		try {
			// svPayloadFromForm enforces the exclusive role: service XOR add-on.
			const payload = svPayloadFromForm(form, merchant.id)
			const { error } = id
				? await supabase.from('service_types').update(payload).eq('id', id)
				: await supabase.from('service_types').insert(payload)
			if (error) throw error
			await loadData()
			setActiveTab(form.type === 'addon' ? 'addons' : 'services')
			closeEditor()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to save service')
		} finally {
			setBusy(false)
		}
	}

	const deleteService = async (id) => {
		setBusy(true)
		setErrorMessage('')
		try {
			const { error } = await supabase.from('service_types').delete().eq('id', id)
			if (error) throw error
			await loadData()
			setDeletingId(null)
			closeEditor()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to delete service')
		} finally {
			setBusy(false)
		}
	}

	const editingService = editor?.mode === 'edit'
		? services.find((s) => s.id === editor.id)
		: null
	const isAddonTab = activeTab === 'addons'
	const noun = isAddonTab ? 'add-on' : 'service'

	const editorProps = {
		mode: editor?.mode || 'add',
		service: editingService,
		form,
		setField,
		catsByType,
		currencySymbol: cs,
		saving: busy,
		onSave: saveService,
		onDelete: deleteService,
		// "New category…" in the category dropdown opens the manager for the
		// form's own type, which may not be the active tab's.
		onManageCats: (type) => openManage(type, true),
	}

	// ── tabs (shared shape, sized per form factor) ──
	const renderTabs = (mobile) => (
		<Box role="tablist" sx={{
			display: mobile ? 'flex' : 'inline-flex', gap: '3px', p: '4px',
			bgcolor: L.surface, borderRadius: '12px',
			...(mobile ? { mt: '12px' } : { border: `1px solid ${L.border}` }),
		}}>
			{[['services', 'Services', counts.services], ['addons', 'Add-ons', counts.addons]].map(([id, label, n]) => {
				const on = activeTab === id
				return (
					<Box
						component="button"
						type="button"
						role="tab"
						aria-selected={on}
						key={id}
						onClick={() => { setActiveTab(id); setDeletingId(null) }}
						sx={{
							display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
							gap: '7px', height: mobile ? 38 : 34, px: '14px', border: 'none',
							borderRadius: '9px', bgcolor: on ? '#FFFFFF' : 'transparent',
							color: on ? L.text : L.muted, fontSize: mobile ? '14px' : '13px',
							fontWeight: 700, fontFamily: 'inherit', cursor: 'pointer',
							boxShadow: on ? '0 1px 3px rgba(46,42,64,0.10)' : 'none',
							...(mobile ? { flex: 1 } : {}),
							'&:hover': { color: L.text },
						}}
					>
						<span>{label}</span>
						<Box component="span" sx={{
							display: 'inline-flex', alignItems: 'center', height: 20, px: '7px',
							borderRadius: '99px',
							bgcolor: on ? L.primarySoft : '#E7E2F3',
							color: on ? L.primaryDeep : '#6E6886',
							fontSize: '11px', fontWeight: 750,
						}}>
							{n}
						</Box>
					</Box>
				)
			})}
		</Box>
	)

	const renderEmptyState = () => (
		<Box sx={{
			p: '44px 16px', textAlign: 'center', color: L.muted, fontWeight: 600,
			fontSize: '13.5px', bgcolor: '#FFFFFF', border: `1px dashed ${L.border}`,
			borderRadius: '14px', display: 'flex', flexDirection: 'column',
			alignItems: 'center', gap: '14px',
		}}>
			{q ? (
				<span>No {isAddonTab ? 'add-ons' : 'services'} match “{searchQuery}”.</span>
			) : (
				<>
					<span>Add your first {noun} to get started.</span>
					<Box
						component="button"
						type="button"
						onClick={() => openAdd({ type: isAddonTab ? 'addon' : 'service' })}
						sx={svPrimaryBtnSx}
					>
						<SvIc name="plus" size={15} sw={2} />
						<span>{isAddonTab ? 'New add-on' : 'New service'}</span>
					</Box>
				</>
			)}
		</Box>
	)

	// ── desktop service row ──
	const renderRow = (s) => {
		if (deletingId === s.id) {
			return (
				<Box key={s.id} sx={{
					display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'center',
					gap: '14px', p: '11px 16px', borderBottom: '1px solid #F5F3FB',
					'&:last-child': { borderBottom: 'none' },
				}}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
						<Box sx={svDotSx(s.appointment_color)} />
						<Box component="span" sx={{
							fontSize: '13.5px', fontWeight: 700, whiteSpace: 'nowrap',
							overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{s.name}
						</Box>
					</Box>
					<Box sx={{
						display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px',
						fontSize: '13px', fontWeight: 650, color: DANGER,
					}}>
						<span>Delete this {s.is_add_on ? 'add-on' : 'service'}?</span>
						<Box
							component="button"
							type="button"
							disabled={busy}
							onClick={() => deleteService(s.id)}
							sx={{
								display: 'inline-flex', alignItems: 'center', height: 32, px: '13px',
								border: 'none', borderRadius: '9px', bgcolor: DANGER, color: '#FFFFFF',
								fontSize: '12.5px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit',
								'&:hover': { bgcolor: DANGER_DEEP },
								'&:disabled': { opacity: 0.6, cursor: 'default' },
							}}
						>
							Delete
						</Box>
						<Box
							component="button"
							type="button"
							onClick={() => setDeletingId(null)}
							sx={{
								display: 'inline-flex', alignItems: 'center', height: 32, px: '13px',
								border: `1.5px solid ${L.border}`, borderRadius: '9px', bgcolor: '#FFFFFF',
								color: L.text, fontSize: '12.5px', fontWeight: 650, cursor: 'pointer',
								fontFamily: 'inherit',
								'&:hover': { borderColor: L.muted },
							}}
						>
							Cancel
						</Box>
					</Box>
				</Box>
			)
		}
		return (
			<Box
				key={s.id}
				onDoubleClick={() => openEdit(s)}
				sx={{
					// duration column fits the widest label ("No extra time")
					display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 104px 80px 72px',
					alignItems: 'center', gap: '14px', p: '11px 16px', borderBottom: '1px solid #F5F3FB',
					'&:last-child': { borderBottom: 'none' },
					'&:hover': { bgcolor: '#FBFAFE' },
					'&:hover .sv-rowactions, &:focus-within .sv-rowactions': { opacity: 1 },
				}}
			>
				<Box sx={{ display: 'flex', alignItems: 'center', gap: '11px', minWidth: 0 }}>
					<Box sx={svDotSx(s.appointment_color)} />
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: '1px', minWidth: 0 }}>
						<Box component="span" sx={{
							fontSize: '13.5px', fontWeight: 700, letterSpacing: '-0.005em',
							whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
						}}>
							{s.name}
						</Box>
						{s.description && (
							<Box component="span" sx={{
								fontSize: '12px', fontWeight: 600, color: '#9A93B4',
								whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
							}}>
								{s.description}
							</Box>
						)}
					</Box>
				</Box>
				<Box sx={{ fontSize: '13px', fontWeight: 600, color: '#6E6886', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
					{formatDuration(s.duration_minutes)}
				</Box>
				<Box sx={{ fontSize: '13.5px', fontWeight: 750, textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
					{formatPrice(s.price)}
				</Box>
				<Box className="sv-rowactions" sx={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', opacity: 0, transition: 'opacity .12s' }}>
					<Box component="button" type="button" title="Edit" onClick={() => openEdit(s)} sx={iconBtnSx}>
						<SvIc name="pencil" size={14} />
					</Box>
					<Box
						component="button"
						type="button"
						title="Delete"
						onClick={() => setDeletingId(s.id)}
						sx={{ ...iconBtnSx, '&:hover': { color: DANGER, borderColor: DANGER, bgcolor: '#FBECEB' } }}
					>
						<SvIc name="trash" size={14} />
					</Box>
				</Box>
			</Box>
		)
	}

	// ── desktop category panel ──
	const renderCategoryPanel = (g) => {
		const real = g.cat.id !== null
		const isCollapsed = collapsed[g.cat.id || 'loose']
		return (
			<CategoryPanel
				key={activeTab + '-' + (g.cat.id || 'loose')}
				g={g}
				real={real}
				isCollapsed={isCollapsed}
				onToggleCollapse={() =>
					setCollapsed((prev) => ({ ...prev, [g.cat.id || 'loose']: !prev[g.cat.id || 'loose'] }))
				}
				dragId={dragId}
				setDragId={setDragId}
				onDragOverCat={(overId) => reorderCat(dragId, overId)}
				onDragEnd={persistCatOrder}
				onRename={renameCat}
				addLabel={isAddonTab ? 'Add add-on' : 'Add service'}
				onAddTo={() => openAdd({ catId: g.cat.id, type: isAddonTab ? 'addon' : 'service' })}
				query={q}
				renderRow={renderRow}
			/>
		)
	}

	// ── mobile list row ──
	const renderMobileRow = (s) => (
		<Box
			component="button"
			type="button"
			key={s.id}
			onClick={() => openEdit(s)}
			sx={{
				display: 'flex', alignItems: 'center', gap: '11px', width: '100%', textAlign: 'left',
				p: '12px 14px 12px 16px', bgcolor: 'transparent', border: 'none',
				borderTop: '1px solid #F5F3FB', minHeight: 56, cursor: 'pointer', fontFamily: 'inherit',
				'&:active': { bgcolor: '#FBFAFE' },
			}}
		>
			<Box sx={svDotSx(s.appointment_color)} />
			<Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '1px' }}>
				<Box component="strong" sx={{
					fontSize: '14.5px', fontWeight: 700, letterSpacing: '-0.005em', color: L.text,
					whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
				}}>
					{s.name}
				</Box>
				<Box component="span" sx={{ fontSize: '12.5px', fontWeight: 600, color: L.muted }}>
					{formatDuration(s.duration_minutes)} · {formatPrice(s.price)}
				</Box>
			</Box>
			<Box sx={{ display: 'flex', color: '#C4BDD6', flex: 'none' }}>
				<SvIc name="chev" size={14} sw={1.8} />
			</Box>
		</Box>
	)

	// ── mobile list screen ──
	const renderMobileList = () => (
		<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
			<Box component="header" sx={{ flex: 'none', p: '14px 16px 12px', bgcolor: L.bg, borderBottom: `1px solid ${L.border}` }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
					<Box component="h1" sx={{ fontSize: '30px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>
						Services
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Box
							component="button"
							type="button"
							aria-label="Manage categories"
							onClick={() => openManage(isAddonTab ? 'addon' : 'service')}
							sx={{
								width: 40, height: 40, flex: 'none', border: `1.5px solid ${L.border}`,
								borderRadius: '11px', bgcolor: '#FFFFFF', color: L.muted,
								display: 'flex', alignItems: 'center', justifyContent: 'center',
								cursor: 'pointer', fontFamily: 'inherit',
								'&:active': { bgcolor: L.surface, color: L.text },
							}}
						>
							<SvIc name="layers" size={17} />
						</Box>
						<Box
							component="button"
							type="button"
							onClick={() => openAdd({ type: isAddonTab ? 'addon' : 'service' })}
							sx={{
								display: 'inline-flex', alignItems: 'center', gap: '6px', height: 40, px: '15px',
								border: 'none', borderRadius: '11px', bgcolor: L.primary, color: L.onPrimary,
								fontSize: '14px', fontWeight: 700, boxShadow: '0 2px 8px rgba(124,107,217,0.28)',
								cursor: 'pointer', fontFamily: 'inherit',
								'&:active': { bgcolor: L.primaryDeep },
							}}
						>
							<SvIc name="plus" size={15} sw={2} />
							<span>New</span>
						</Box>
					</Box>
				</Box>
				{renderTabs(true)}
				<Box component="label" sx={{
					display: 'flex', alignItems: 'center', gap: '8px', mt: '12px', height: 40,
					p: '0 12px', borderRadius: '11px', bgcolor: L.surface, color: L.muted,
				}}>
					<SvIc name="search" size={16} />
					<Box
						component="input"
						type="text"
						placeholder={isAddonTab ? 'Search add-ons' : 'Search services'}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						sx={{
							border: 'none', outline: 'none', bgcolor: 'transparent', fontFamily: 'inherit',
							fontSize: '15px', color: L.text, width: '100%',
							'&::placeholder': { color: L.muted },
						}}
					/>
				</Box>
			</Box>

			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '12px 16px 20px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
				{errorMessage && (
					<Alert severity="error" onClose={() => setErrorMessage('')}>{errorMessage}</Alert>
				)}
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
						<CircularProgress size={24} sx={{ color: L.primary }} />
					</Box>
				) : (
					<>
						{groups.map((g) => (
							<Box
								component="section"
								key={activeTab + '-' + (g.cat.id || 'loose')}
								sx={{
									// flex: 'none' so each card keeps its natural height and the
									// list scrolls — otherwise column flex shrinks the cards and
									// overflow:hidden clips the rows inside them.
									flex: 'none',
									bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '16px',
									boxShadow: '0 1px 3px rgba(46,42,64,0.04)', overflow: 'hidden',
								}}
							>
								<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '13px 8px 10px 16px' }}>
									<Box component="strong" sx={{ fontSize: '15.5px', fontWeight: 800, letterSpacing: '-0.01em' }}>
										{g.cat.name}
									</Box>
									<Box component="span" sx={countPillSx}>{g.items.length}</Box>
									<Box
										component="button"
										type="button"
										aria-label={'Add to ' + g.cat.name}
										onClick={() => openAdd({ catId: g.cat.id, type: isAddonTab ? 'addon' : 'service' })}
										sx={{
											ml: 'auto', width: 34, height: 34, flex: 'none', border: 'none',
											borderRadius: '9px', bgcolor: 'transparent', color: L.primary,
											display: 'flex', alignItems: 'center', justifyContent: 'center',
											cursor: 'pointer', fontFamily: 'inherit',
											'&:active': { bgcolor: L.primarySoft },
										}}
									>
										<SvIc name="plus" size={14} sw={2.1} />
									</Box>
								</Box>
								{g.items.map(renderMobileRow)}
								{g.items.length === 0 && (
									<Box sx={{ p: '14px 16px 16px', fontSize: '13px', fontWeight: 600, color: '#B4ADC6', borderTop: '1px solid #F5F3FB' }}>
										Nothing here yet
									</Box>
								)}
							</Box>
						))}
						{groups.length === 0 && renderEmptyState()}
					</>
				)}
			</Box>
		</Box>
	)

	// ── mobile ──
	if (isMobile) {
		return (
			<Box sx={{ height: '100%', bgcolor: L.bg, color: L.text, fontFamily: L.font, fontSize: '14px', overflow: 'hidden' }}>
				{mobileScreen === 'list' && renderMobileList()}
				{/* Sub-screens (service/add-on form, categories) render as full-screen
				    fixed overlays so the app top bar is hidden, matching the staff
				    management sub-pages. */}
				{mobileScreen === 'form' && (
					<Box sx={{ position: 'fixed', inset: 0, zIndex: 1100, bgcolor: L.bg, overflow: 'hidden' }}>
						<SvmFormScreen key={editor?.mode === 'edit' ? editor.id : 'add'} {...editorProps} onBack={closeEditor} />
					</Box>
				)}
				{mobileScreen === 'cats' && (
					<Box sx={{ position: 'fixed', inset: 0, zIndex: 1100, bgcolor: L.bg, overflow: 'hidden' }}>
						<SvmCatsScreen
							title={manageType === 'addon' ? 'Add-on categories' : 'Service categories'}
							cats={managedCats}
							countFor={countFor}
							saving={busy}
							focusAdd={manageFromForm}
							onBack={closeManage}
							onMove={moveCat}
							onRename={renameCat}
							onAdd={addCat}
							onDelete={deleteCat}
						/>
					</Box>
				)}
			</Box>
		)
	}

	// ── desktop workspace ──
	return (
		<Box sx={{
			height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0,
			bgcolor: '#FBFAFE', borderRadius: '12px', overflow: 'hidden',
			color: L.text, fontFamily: L.font, fontSize: '14px',
		}}>
			<Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', p: '22px 26px 30px' }}>
				{/* Page head */}
				<Box sx={{
					display: 'flex', alignItems: 'center', justifyContent: 'space-between',
					gap: '16px', mb: '18px', flexWrap: 'wrap',
				}}>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
						<Box component="h1" sx={{ fontSize: '23px', fontWeight: 800, m: 0, letterSpacing: '-0.02em' }}>
							Services
						</Box>
						{renderTabs(false)}
					</Box>
					<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						<Box component="label" sx={{
							display: 'flex', alignItems: 'center', gap: '8px', height: 40, width: 230,
							p: '0 13px', borderRadius: '10px', bgcolor: '#FFFFFF',
							border: `1.5px solid ${L.border}`, color: L.muted,
							'&:focus-within': { borderColor: L.primary },
						}}>
							<SvIc name="search" size={15} />
							<Box
								component="input"
								type="text"
								placeholder={isAddonTab ? 'Search add-ons' : 'Search services'}
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								sx={{
									border: 'none', outline: 'none', bgcolor: 'transparent', fontFamily: 'inherit',
									fontSize: '13.5px', color: L.text, width: '100%',
									'&::placeholder': { color: L.muted },
								}}
							/>
						</Box>
						<Box component="button" type="button" onClick={() => openManage(isAddonTab ? 'addon' : 'service')} sx={svGhostBtnSx}>
							<SvIc name="layers" size={15} />
							<span>Manage categories</span>
						</Box>
						<Box
							component="button"
							type="button"
							onClick={() => openAdd({ type: isAddonTab ? 'addon' : 'service' })}
							sx={svPrimaryBtnSx}
						>
							<SvIc name="plus" size={15} sw={2} />
							<span>{isAddonTab ? 'New add-on' : 'New service'}</span>
						</Box>
					</Box>
				</Box>

				{errorMessage && (
					<Alert severity="error" onClose={() => setErrorMessage('')} sx={{ mb: 2 }}>
						{errorMessage}
					</Alert>
				)}

				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
						<CircularProgress size={26} sx={{ color: L.primary }} />
					</Box>
				) : (
					<>
						{groups.map(renderCategoryPanel)}
						{groups.length === 0 && renderEmptyState()}
					</>
				)}
			</Box>

			{/* Add/Edit drawer */}
			{editor && (
				<SvDrawer key={editor.mode === 'edit' ? editor.id : 'add'} {...editorProps} onClose={closeEditor} />
			)}

			{/* Manage categories dialog — can sit above the add/edit drawer */}
			{manageType && (
				<SvManageDialog
					title={manageType === 'addon' ? 'Add-on categories' : 'Service categories'}
					cats={managedCats}
					countFor={countFor}
					saving={busy}
					focusAdd={manageFromForm}
					onClose={closeManage}
					onReorder={reorderCat}
					onDragEnd={persistCatOrder}
					onRename={renameCat}
					onAdd={addCat}
					onDelete={deleteCat}
				/>
			)}
		</Box>
	)
}

/* ── desktop category panel (sv-cat) — local rename state ── */
function CategoryPanel({ g, real, isCollapsed, onToggleCollapse, dragId, setDragId, onDragOverCat, onDragEnd, onRename, addLabel, onAddTo, query, renderRow }) {
	const [renaming, setRenaming] = useState(false)
	const commit = (v) => {
		setRenaming(false)
		const name = v.trim()
		if (name && name !== g.cat.name) onRename(g.cat.id, name)
	}

	return (
		<Box
			component="section"
			onDragOver={(e) => {
				if (dragId && dragId !== g.cat.id && real) {
					e.preventDefault()
					onDragOverCat(g.cat.id)
				}
			}}
			sx={{
				bgcolor: '#FFFFFF', border: `1px solid ${L.border}`, borderRadius: '14px',
				mb: '14px', boxShadow: '0 1px 3px rgba(46,42,64,0.04)',
				// dragId is only ever set to a real category's id (see the drag
				// handle below), but both dragId's initial/reset value and the
				// virtual Uncategorized bucket's cat.id are null — guard with
				// `real` so that null === null doesn't dim Uncategorized at rest.
				...(real && dragId === g.cat.id ? { opacity: 0.45, borderStyle: 'dashed', borderColor: L.primary } : {}),
				'&:hover .sv-catactions, &:focus-within .sv-catactions': { opacity: 1 },
			}}
		>
			<Box component="header" sx={{
				display: 'flex', alignItems: 'center', gap: '7px', p: '10px 16px 10px 8px',
				borderBottom: '1px solid #EFEBF7',
			}}>
				{real ? (
					<Box
						component="span"
						title="Drag to reorder"
						draggable
						onDragStart={(e) => { setDragId(g.cat.id); e.dataTransfer.effectAllowed = 'move' }}
						onDragEnd={() => { setDragId(null); onDragEnd && onDragEnd() }}
						sx={{
							width: 24, height: 32, flex: 'none', display: 'flex', alignItems: 'center',
							justifyContent: 'center', color: '#C4BDD6', borderRadius: '8px', cursor: 'grab',
							'&:hover': { bgcolor: L.surface, color: L.muted },
						}}
					>
						<SvIc name="grip" size={15} />
					</Box>
				) : (
					<Box component="span" sx={{ width: 24, height: 32, flex: 'none' }} />
				)}
				<Box
					component="button"
					type="button"
					aria-label={isCollapsed ? 'Expand category' : 'Collapse category'}
					onClick={onToggleCollapse}
					sx={{
						width: 26, height: 26, flex: 'none', border: 'none', borderRadius: '7px',
						bgcolor: 'transparent', color: L.muted, display: 'flex', alignItems: 'center',
						justifyContent: 'center', transition: 'transform .15s', cursor: 'pointer',
						fontFamily: 'inherit',
						transform: isCollapsed ? 'rotate(-90deg)' : 'none',
						'&:hover': { bgcolor: L.surface, color: L.text },
					}}
				>
					<SvIc name="down" size={14} sw={1.9} />
				</Box>
				{renaming ? (
					<Box
						component="input"
						autoFocus
						defaultValue={g.cat.name}
						onBlur={(e) => commit(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') commit(e.target.value)
							if (e.key === 'Escape') setRenaming(false)
						}}
						sx={{
							height: 32, border: `1.5px solid ${L.primary}`, borderRadius: '8px',
							bgcolor: '#FFFFFF', p: '0 10px', fontFamily: 'inherit', fontSize: '14px',
							fontWeight: 700, color: L.text, outline: 'none', minWidth: 0, width: 200,
						}}
					/>
				) : (
					<Box component="strong" sx={{ fontSize: '15px', fontWeight: 800, letterSpacing: '-0.01em' }}>
						{g.cat.name}
					</Box>
				)}
				<Box component="span" sx={countPillSx}>{g.items.length}</Box>
				<Box sx={{ flex: 1 }} />
				{real && (
					<Box className="sv-catactions" sx={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: 0, transition: 'opacity .12s' }}>
						<Box component="button" type="button" title="Rename category" onClick={() => setRenaming(true)} sx={iconBtnSx}>
							<SvIc name="pencil" size={13} />
						</Box>
					</Box>
				)}
				<Box
					component="button"
					type="button"
					onClick={onAddTo}
					sx={{
						display: 'inline-flex', alignItems: 'center', gap: '6px', flex: 'none', border: 'none',
						bgcolor: 'transparent', color: L.primary, fontSize: '12.5px', fontWeight: 700,
						p: '6px 9px', borderRadius: '7px', whiteSpace: 'nowrap', cursor: 'pointer',
						fontFamily: 'inherit', ml: '4px',
						'&:hover': { color: L.primaryDeep, bgcolor: L.primarySoft },
					}}
				>
					<SvIc name="plus" size={13} sw={2.1} />
					<span>{addLabel}</span>
				</Box>
			</Box>
			{!isCollapsed && (
				<Box>
					{g.items.map(renderRow)}
					{g.items.length === 0 && (
						<Box sx={{ p: '18px 16px', fontSize: '13px', fontWeight: 600, color: '#B4ADC6' }}>
							{query ? 'No matches in this category.' : 'Nothing in this category yet.'}
						</Box>
					)}
				</Box>
			)}
		</Box>
	)
}
